import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import CommonModal from "../../../components/common/CommonModal";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import HomeworkFormModal from "../../../components/Modals/HomeworkFormModal";
import HomeworkViewModal from "../../../components/Modals/HomeworkViewModal";
import { useAuth } from "../../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";
import { useError } from "../../../contexts/ErrorContext";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useSubjectsByClass } from "../../../hooks/useSubjects";
import {
  HomeworkRequestDto,
  HomeworkSummaryDto,
  useHomeworkByClass,
  useHomeworkDetail,
  useHomeworkMutations,
} from "../../../hooks/useHomework";

const HomeworkManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user, isTeacher, isSchoolAdmin } = useAuth();
  const { showError, showSuccess } = useError();

  const { data: schoolIdFromAdmin } = useSchoolIdForAdmin(isSchoolAdmin() ? undefined : user?.id);
  const schoolId = useMemo(() => {
    if (isTeacher()) return user?.schoolId ?? undefined;
    if (user?.schoolId) return user.schoolId;
    return schoolIdFromAdmin ?? undefined;
  }, [user?.schoolId, schoolIdFromAdmin, isTeacher]);

  const { selectedClass, selectedSession } = useSessionClass();
  const classId = selectedClass?.id;

  const [subjectFilter, setSubjectFilter] = useState<number | "">("");
  const [searchTerm, setSearchTerm] = useState("");

  // Reset subject filter whenever the header's class selection changes
  useEffect(() => {
    setSubjectFilter("");
  }, [classId]);

  // Subjects are keyed by class name (section-agnostic), not the section-specific class id
  const subjectFilterQuery = useSubjectsByClass(selectedClass?.name || "");

  const listQuery = useHomeworkByClass(schoolId, classId, subjectFilter || undefined);

  const { createHomeworkMutation, updateHomeworkMutation, deleteHomeworkMutation } =
    useHomeworkMutations();

  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [detailMode, setDetailMode] = useState<"edit" | "view" | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [homeworkToDelete, setHomeworkToDelete] = useState<number | null>(null);

  const detailQuery = useHomeworkDetail(detailId ?? undefined);

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setDetailMode(null);
    setDetailId(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (row: HomeworkSummaryDto) => {
    setIsEdit(true);
    setDetailMode("edit");
    setDetailId(row.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDetailId(null);
    setDetailMode(null);
  };

  const handleOpenViewModal = (row: HomeworkSummaryDto) => {
    setDetailMode("view");
    setDetailId(row.id);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setDetailId(null);
    setDetailMode(null);
  };

  const handleSubmit = (dto: HomeworkRequestDto, files: File[]) => {
    if (isEdit && detailId) {
      updateHomeworkMutation.mutate(
        { id: detailId, dto, files },
        {
          onSuccess: () => {
            showSuccess(t("homework_updated_successfully") || "Homework updated successfully");
            handleCloseModal();
          },
          onError: (error: unknown) => {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            showError(
              err?.response?.data?.message || err?.message || t("error_updating_homework") || "Error updating homework"
            );
          },
        }
      );
    } else {
      createHomeworkMutation.mutate(
        { dto, files },
        {
          onSuccess: () => {
            showSuccess(t("homework_added_successfully") || "Homework assigned successfully");
            handleCloseModal();
          },
          onError: (error: unknown) => {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            showError(
              err?.response?.data?.message || err?.message || t("error_adding_homework") || "Error assigning homework"
            );
          },
        }
      );
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setHomeworkToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setHomeworkToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (homeworkToDelete == null) return;
    deleteHomeworkMutation.mutate(homeworkToDelete, {
      onSuccess: () => {
        showSuccess(t("homework_deleted_successfully") || "Homework deleted successfully");
        handleCloseDeleteDialog();
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        showError(
          err?.response?.data?.message || err?.message || t("error_deleting_homework") || "Error deleting homework"
        );
      },
    });
  };

  const columns = [
    { id: "subjectName", label: t("subject") || "Subject", minWidth: 120 },
    { id: "title", label: t("title") || "Title", minWidth: 160 },
    { id: "dueDate", label: t("due_date") || "Due Date", minWidth: 120 },
    { id: "assignedByName", label: t("assigned_by") || "Assigned By", minWidth: 130 },
    {
      id: "attachmentCount",
      label: t("attachments") || "Attachments",
      minWidth: 100,
      align: "center" as const,
      format: (value: number) => (value ? <Chip size="small" label={value} /> : "-"),
    },
  ];

  const filteredData = useMemo(() => {
    // The class-scoped homework API isn't session-aware (a class id persists across sessions),
    // so filter to the header's currently selected session on the client.
    const sessionScoped = (listQuery.data || []).filter(
      (row) => !selectedSession?.id || row.sessionId === selectedSession.id
    );
    if (!searchTerm) return sessionScoped;
    const term = searchTerm.toLowerCase().trim();
    return sessionScoped.filter(
      (row) =>
        String(row.title || "").toLowerCase().includes(term) ||
        String(row.className || "").toLowerCase().includes(term) ||
        String(row.subjectName || "").toLowerCase().includes(term) ||
        String(row.assignedByName || "").toLowerCase().includes(term)
    );
  }, [listQuery.data, searchTerm, selectedSession?.id]);

  const isSubmitting = createHomeworkMutation.isLoading || updateHomeworkMutation.isLoading;

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
            {t("homework_management") || "Homework Management"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 140, "& .MuiInputBase-root": { height: 36 } }} disabled={!classId}>
              <InputLabel>{t("subject") || "Subject"}</InputLabel>
              <Select
                label={t("subject") || "Subject"}
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <MenuItem value="">{t("all_subjects") || "All Subjects"}</MenuItem>
                {(subjectFilterQuery.data ?? []).map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder={t("search") || "Search"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, "& .MuiInputBase-root": { height: 36 } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
              onClick={handleOpenAddModal}
            >
              {t("assign_homework") || "Assign Homework"}
            </Button>
          </Box>
        </Box>

        {!classId ? (
          <Alert severity="info" sx={{ mb: 1, py: 0.5, px: 1.5 }}>
            {t("please_select_class_from_header") || "Please select a class from the header to view homework"}
          </Alert>
        ) : listQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : listQuery.error ? (
          <Alert severity="error" sx={{ mb: 1, py: 0.5, px: 1.5 }}>
            {listQuery.error.message || t("error_loading_homework") || "Error loading homework"}
          </Alert>
        ) : (
          <EnhancedTable
            columns={columns}
            data={filteredData}
            searchFields={[]}
            filterFields={[]}
            actions={(row: HomeworkSummaryDto) => (
              <ActionButtons
                showView
                onView={() => handleOpenViewModal(row)}
                onEdit={() => handleOpenEditModal(row)}
                onDelete={() => handleOpenDeleteDialog(row.id)}
                editTooltip={t("edit") || "Edit"}
                deleteTooltip={t("delete") || "Delete"}
                viewTooltip={t("view") || "View"}
              />
            )}
            cardProps={{
              elevation: 0,
              sx: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 0,
                "& .MuiCardContent-root": { p: 0, "&:last-child": { pb: 0 } },
              },
            }}
          />
        )}
      </Paper>

      <HomeworkFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        initialData={detailMode === "edit" ? detailQuery.data : undefined}
        schoolId={schoolId}
      />

      <HomeworkViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        homework={detailMode === "view" ? detailQuery.data : undefined}
        loading={detailMode === "view" && detailQuery.isLoading}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        title={t("confirm_delete") || "Confirm Delete"}
        maxWidth="sm"
        onSubmit={handleConfirmDelete}
        submitLabel={t("delete") || "Delete"}
        submitDisabled={deleteHomeworkMutation.isLoading}
        danger
      >
        <Typography variant="body1" color="text.secondary">
          {t("are_you_sure_you_want_to_delete_this_homework") ||
            "Are you sure you want to delete this homework?"}
        </Typography>
      </CommonModal>
    </>
  );
};

export default HomeworkManagement;
